import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { BadgeCheck, Mail, Pencil, X } from "lucide-react";
import { API_URL } from "../../api/api";
import {
  uploadFileInChunks,
  cancelChunkedUpload,
} from "../../api/chunkedUpload";
import FileUpload from "./FileUpload";
import { photoUrl } from "../../layout/UserAvatar";
import { todayStr as localToday } from "../../utils/localDate";
import {
  EMAIL_RE,
  parseAlternateEmails,
} from "../../utils/alternateEmails";
import {
  accountNumberMaxLength,
  sanitizeAccountNumberInput,
  validateAccountNumber,
} from "../../utils/accountNumber";
import {
  DEFAULT_PROFICIENCY,
  PROFICIENCY_LEVELS,
  SKILL_CERT_ACCEPT,
  certificateName,
  formatSkillDate,
  legacyCertification,
  levelTone,
  makeSkillRow,
  parseSkills,
  serializeSkills,
  validateCertificate,
} from "../../utils/skills";
import { openMemberDocument } from "../../utils/memberDocuments";
import { toast } from "../../utils/toast";
import IfscField from "../../components/em/IfscField";
import "./teams.css";

import { confirmDialog } from "../../components/em/confirm";
// Common country dial-codes. "Other" lets the user type a custom prefix.
// Order: India first since the team is India-based; then a sensible mix.
const COUNTRY_CODES = [
  { label: "🇮🇳 India", code: "+91" },
  { label: "🇺🇸 USA", code: "+1" },
  { label: "🇬🇧 UK", code: "+44" },
  { label: "🇦🇪 UAE", code: "+971" },
  { label: "🇸🇬 Singapore", code: "+65" },
  { label: "🇦🇺 Australia", code: "+61" },
  { label: "🇨🇦 Canada", code: "+1" },
  { label: "🇩🇪 Germany", code: "+49" },
  { label: "🇫🇷 France", code: "+33" },
  { label: "🇯🇵 Japan", code: "+81" },
  { label: "🇨🇳 China", code: "+86" },
  { label: "🇧🇷 Brazil", code: "+55" },
  { label: "🇿🇦 South Africa", code: "+27" },
  { label: "🇸🇦 Saudi Arabia", code: "+966" },
  { label: "🇲🇾 Malaysia", code: "+60" },
  { label: "🌐 Other", code: "" },
];

// The skills panel is one form for two jobs: adding a skill and editing one.
// `certificate` and `savedIndex` ride along so an edit keeps the certificate
// already on file (and can still open it) when no new one is picked.
const emptySkillDraft = () => ({
  name: "",
  level: DEFAULT_PROFICIENCY,
  program: "",
  completed_on: "",
  certificate: "",
  savedIndex: null,
});

// Written from the skills list on submit, never from `form` — they are still
// keys of `form` because the record comes back with them, and sending both
// copies would let the stale one win.
const SKILL_OWNED_FIELDS = new Set([
  "skills",
  "program_name",
  "comp_date",
  "certificate",
]);

// Added addresses are kept as {id, value} rather than bare strings so each
// <input> has a stable React key — a row stays editable after it is added, and
// removing one in the middle must not re-mount the rows below it and steal
// focus mid-typing.
let altEmailSeq = 0;
const makeAltRow = (value = "") => ({ id: `alt-${(altEmailSeq += 1)}`, value });

// Added rows plus whatever is still sitting in the open row, trimmed, blank-free
// and de-duplicated case-insensitively. The draft is folded in so an address
// typed but never committed with Enter/Add is still saved rather than lost.
// This is what gets sent; the server normalises again
// (utils/alternateEmails) since the API is reachable without this form.
const cleanAltList = (rows = [], draft = "") => {
  const seen = new Set();
  const out = [];
  [...rows.map((r) => r.value), draft].forEach((entry) => {
    const value = String(entry || "").trim();
    const key = value.toLowerCase();
    if (!value || seen.has(key)) return;
    seen.add(key);
    out.push(value);
  });
  return out;
};

// Small renderer for an existing file: filename + open + remove. Sits next
// to a regular file input so users see what's already on file and can
// replace or clear it.
// const ExistingFileChip = ({ path, onRemove }) => {
//   if (!path) return null;
//   const url = /^https?:\/\//.test(path)
//     ? path
//     : `${API_URL}${path.startsWith("/") ? "" : "/"}${path}`;
//   const filename = String(path).split("/").pop();
//   return (
//     <div
//       style={{
//         display: "inline-flex",
//         gap: 8,
//         alignItems: "center",
//         marginTop: 6,
//         padding: "6px 10px",
//         background: "#f8fafc",
//         border: "1px solid #e2e8f0",
//         borderRadius: 8,
//         fontSize: 12,
//       }}
//     >
//       <a
//         href={url}
//         target="_blank"
//         rel="noreferrer"
//         style={{ color: "var(--em-fg2)", textDecoration: "none" }}
//       >
//         📎 {filename}
//       </a>
//       <button
//         type="button"
//         onClick={onRemove}
//         style={{
//           border: 0,
//           background: "transparent",
//           color: "#dc2626",
//           cursor: "pointer",
//           fontSize: 12,
//           padding: 0,
//         }}
//       >
//         Remove
//       </button>
//     </div>
//   );
// };

const TeamFormWizard = ({
  editData,
  defaultValues,
  mode = "admin",
  token,
  onClose,
  onEdit,
  onSaved,
}) => {
  const isView = mode === "view";
  const isOnboarding = mode === "onboarding";

  // Saving keeps the form open, so a wizard that just created an employee has
  // to remember the new id — otherwise the next save would create a second
  // record instead of updating the first. Everything that addresses the stored
  // record (documents, the login email lock) reads this, not editData.
  const [createdId, setCreatedId] = useState(null);
  const recordId = editData?.id ?? createdId;

  // The email is the account's login identity and is already verified once the
  // record exists, so it stays read-only here — changing it would orphan the
  // login. Only a brand-new employee can have one typed in — and only until
  // the first save creates the record.
  const emailLocked = !!recordId;

  // An employee maintains their own Personal details and their own Skills —
  // what they can do is their account to keep, and everyone edits it. What HR
  // owns is Employment, Documents and Exit, so members see those read-only.
  // The server enforces the same split (utils/selfEdit.SELF_EDITABLE_FIELDS).
  const viewerRole = (localStorage.getItem("role") || "").toLowerCase();
  const restrictedToPersonal = viewerRole === "member" && !isOnboarding;
  const SELF_EDITABLE_STEPS = ["Personal", "Skills"];

  const allSteps = ["Personal", "Employment", "Skills", "Documents", "Exit"];
  const steps = isOnboarding
    ? allSteps.filter((step) => step !== "Exit")
    : allSteps;
  const [currentStep, setCurrentStep] = useState(0);
  const stepLocked =
    restrictedToPersonal && !SELF_EDITABLE_STEPS.includes(steps[currentStep]);
  const [progress, setProgress] = useState(0);
  const [completedCounts, setCompletedCounts] = useState({});
  const [departments, setDepartments] = useState([]);
  const [jobTitles, setJobTitles] = useState([]);
  const [employmentStatus, setEmploymentStatus] = useState([]);
  const [employmentTypes, setEmploymentTypes] = useState([]);
  const [workLocations, setWorkLocations] = useState([]);
  const [workShifts, setWorkShifts] = useState([]);
  const [managerOptions, setManagerOptions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitErr, setSubmitErr] = useState(null);
  const [saving, setSaving] = useState(false);
  // Documents go up one at a time before the save, so the button has to say
  // more than "Saving…" — a tab with several scans takes a moment.
  const [uploadNote, setUploadNote] = useState(null);
  const [dirty, setDirty] = useState(false);
  const initialFormRef = useRef({});
  const photoInputRef = useRef(null);
  const [savedToast, setSavedToast] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  // Today's date in YYYY-MM-DD for HTML5 date max attributes. Local, not
  // toISOString(): east of UTC that returns yesterday for part of the day and
  // would reject a date entered today as "in the future".
  const todayStr = useMemo(() => localToday(), []);

  const isLocked = (field) =>
    mode === "onboarding" &&
    [
      "employeeid",
      "department",
      "jobtitle",
      "reporting_manager",
      "salary",
      "employee_status",
    ].includes(field);
  const [form, setForm] = useState({
    // Personal
    first_name: "",
    last_name: "",
    email: "",
    contact_number: "",
    dob: "",
    gender: "",
    nationality: "",
    address: "",
    blood_group: "",
    password: "",
    resignation_date: "",
    emergency_contact_name: "",
    emergency_contact_relation: "",
    emergency_contact_phone: "",
    // Employment
    role_id: "",
    employeeid: "",
    jobtitle: "",
    doj: "",
    employee_type: "",
    reporting_manager: "",
    primary_manager_id: "",
    dotted_manager_id: "",
    employee_status: "",
    work_location: "",
    work_shift: "",
    designation: "",
    additional_notes: "",
    department: "",
    join_date: "",
    salary: "",
    hourly_cost: "",
    // Skills
    skills: "",
    program_name: "",
    comp_date: "",
    certificate: "",
    // Documents
    resume: "",
    edu_certificates: "",
    other_edu_certificates: "",
    salary_slip: "",
    reli_experience: "",
    offer_letter: "",
    confirmation_letter: "",
    appointment_letter: "",
    employment_contract: "",
    nda_agreement: "",
    pan_card: "",
    pan_card_name: "",
    pan_card_number: "",
    nominee_name: "",
    aadhar_card: "",
    aadhar_name: "",
    aadhar_card_number: "",
    aadhar_dob: "",
    bank_name: "",
    bank_full_name: "",
    bank_account_number: "",
    bank_branch: "",
    bank_ifsc: "",
    // Exit
    exit_date: "",
    exit_reason: "",
    exit_status: "",
    notice_period: "",
    exit_document: "",
  });

  // Optional secondary addresses. Not part of `form` because the wizard edits
  // them as rows while the column stores one JSON array — they are serialised
  // on submit instead (see handleSubmit). The group only ever shows the
  // addresses that exist plus one open row, so it stays roughly as tall as the
  // single fields around it.
  const [altEmails, setAltEmails] = useState([]);
  const [altDraft, setAltDraft] = useState("");

  // Skills are a list of their own, not form fields: one row per skill, each
  // with a proficiency, an optional program + completion date and an optional
  // certificate. The whole list is serialised into the single `skills` column
  // on submit (see handleSubmit), which is why it is not part of `form`.
  const [skillRows, setSkillRows] = useState([]);
  const [skillDraft, setSkillDraft] = useState(emptySkillDraft());
  const [skillDraftFile, setSkillDraftFile] = useState(null);
  // The id of the card the panel is editing, or null while it is adding one.
  const [editingSkillId, setEditingSkillId] = useState(null);
  const [skillError, setSkillError] = useState("");
  const [skillSearch, setSkillSearch] = useState("");

  const [files, setFiles] = useState({
    profile_photo: null,
    resume: null,
    id_proof: null,
    certificate: null,
    edu_certificates: null,
    pan_card: null,
  });

  const [previewPhoto, setPreviewPhoto] = useState(null);
  // A stored photo whose file is gone (404) must not leave the alt text
  // sitting over the avatar — fall back to initials, as UserAvatar does.
  const [photoBroken, setPhotoBroken] = useState(false);
  // Every route that changes the photo goes through here so the broken flag
  // is always cleared alongside it.
  const showPhoto = (src) => {
    setPhotoBroken(false);
    setPreviewPhoto(src);
  };

  const stepFields = {
    0: [
      "first_name",
      "last_name",
      "email",
      "contact_number",
      "dob",
      "gender",
      "nationality",
      "address",
      "blood_group",
      "alternate_emails",
      "emergency_contact_name",
      "emergency_contact_phone",
      "emergency_contact_relation",
    ],
    1: [
      "role_id",
      "employeeid",
      "jobtitle",
      "doj",
      "employee_type",
      "reporting_manager",
      "employee_status",
      "work_location",
      "work_shift",
      "designation",
      "additional_notes",
      "department",
      "join_date",
      "salary",
      "hourly_cost",
    ],
    // One entry, not four: the skills tab is a list now, and it counts as
    // filled as soon as it holds a skill (see calculateStepCompletion).
    2: ["skills"],
    3: [
      "resume",
      "edu_certificates",
      "other_edu_certificates",
      "salary_slip",
      "reli_experience",
      "offer_letter",
      "confirmation_letter",
      "appointment_letter",
      "employment_contract",
      "nda_agreement",
      "pan_card",
      "pan_card_name",
      "pan_card_number",
      "nominee_name",
      "aadhar_card",
      "aadhar_name",
      "aadhar_card_number",
      "aadhar_dob",
      "bank_name",
      "bank_full_name",
      "bank_account_number",
      "bank_branch",
      "bank_ifsc",
    ],
    4: ["exit_date", "exit_reason", "exit_status"],
  };

  // Prefill form if editData exists
  useEffect(() => {
    const data = editData || defaultValues;
    if (data) {
      const fixedData = { ...data };

      // Split fullname if needed
      if (fixedData.fullname && !fixedData.first_name) {
        const parts = fixedData.fullname.split(" ");
        fixedData.first_name = parts[0];
        fixedData.last_name = parts.slice(1).join(" ");
      }

      const dateFields = [
        "dob",
        "doj",
        "join_date",
        "comp_date",
        "aadhar_dob",
        "exit_date",
        "resignation_date",
      ];

      dateFields.forEach((field) => {
        if (fixedData[field]) {
          fixedData[field] = String(fixedData[field]).split("T")[0];
        }
      });

      // NEVER load the bcrypt hash into a visible field. Empty == "no change".
      delete fixedData.password;

      // Alternate emails live in their own row state, not in `form` — leaving
      // the raw JSON in `form` would also append it to the FormData and the
      // server would see the field twice.
      const alts = parseAlternateEmails(fixedData.alternate_emails);
      delete fixedData.alternate_emails;
      setAltEmails(alts.map((e) => makeAltRow(e)));
      setAltDraft("");

      // Same story for skills: the column holds the whole list, and the three
      // legacy single-certification columns are folded into the first entry.
      setSkillRows(
        parseSkills(fixedData.skills, {
          program_name: fixedData.program_name,
          comp_date: fixedData.comp_date,
          certificate: fixedData.certificate,
        }),
      );
      setSkillDraft(emptySkillDraft());
      setSkillDraftFile(null);
      setEditingSkillId(null);
      setSkillError("");
      setSkillSearch("");

      // Parse a country dial-code prefix from contact_number so the picker
      // reflects what was saved. "+91 9876543210" → cc=+91, num=9876543210.
      if (typeof fixedData.contact_number === "string") {
        const m = fixedData.contact_number.match(/^\s*(\+\d{1,4})\s+(.*)$/);
        if (m) {
          fixedData.country_code = m[1];
          fixedData.contact_number = m[2].trim();
        } else if (!fixedData.country_code) {
          fixedData.country_code = "+91";
        }
      } else if (!fixedData.country_code) {
        fixedData.country_code = "+91";
      }

      // Manager IDs come back as numbers from the API; coerce to strings
      // because the <select> options use string ids.
      if (fixedData.primary_manager_id != null)
        fixedData.primary_manager_id = String(fixedData.primary_manager_id);
      if (fixedData.dotted_manager_id != null)
        fixedData.dotted_manager_id = String(fixedData.dotted_manager_id);
      // Coerce role_id to string so the <select> value comparison works.
      if (fixedData.role_id != null)
        fixedData.role_id = String(fixedData.role_id);

      setForm((prev) => ({ ...prev, ...fixedData }));
      // Track the original snapshot so we can detect dirty state.
      initialFormRef.current = { ...fixedData };
      setDirty(false);

      if (fixedData.profile_photo) {
        showPhoto(fixedData.profile_photo);
      }
    }
  }, [editData, defaultValues]);

  /*  useEffect(() => {
    setProgress(((currentStep + 1) / steps.length) * 100);
    calculateStepCompletion();
      fetchDropdownData();
      fetchWorkLocations();
fetchWorkShifts();

  }, [currentStep, form]);*/

  useEffect(() => {
    setProgress(((currentStep + 1) / steps.length) * 100);
    calculateStepCompletion();
  }, [currentStep, form, altEmails, altDraft, skillRows]);

  // Warn before browser navigation when there are unsaved changes.
  useEffect(() => {
    if (!dirty || isView) return undefined;
    const onBefore = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBefore);
    return () => window.removeEventListener("beforeunload", onBefore);
  }, [dirty, isView]);

  const guardClose = async () => {
    if (dirty && !isView) {
      if (!(await confirmDialog("You have unsaved changes. Discard and close?")))
        return;
    }
    if (onClose) onClose();
  };

  useEffect(() => {
    fetchDropdownData();
    fetchWorkLocations();
    fetchWorkShifts();
    fetchManagers();
    fetchRoles();
  }, []);

  const fetchDropdownData = async () => {
    try {
      const [dep, jt, es, et] = await Promise.all([
        axios.get(`${API_URL}/api/settings/departments`),
        axios.get(`${API_URL}/api/settings/job-titles`),
        axios.get(`${API_URL}/api/settings/employment-statuses`),
        axios.get(`${API_URL}/api/settings/employment-types`),
      ]);

      setDepartments(dep.data);
      setJobTitles(jt.data);
      setEmploymentStatus(es.data);
      setEmploymentTypes(et.data);
    } catch (error) {
      console.error("Error loading dropdowns:", error);
    }
  };
  const fetchWorkLocations = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/settings/work-locations`);
      setWorkLocations(res.data || []);
    } catch (err) {
      console.error("Error fetching work locations", err);
    }
  };

  const fetchWorkShifts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/settings/shifts`);
      setWorkShifts(res.data || []);
    } catch (err) {
      console.error("Error fetching shifts", err);
    }
  };

  const fetchRoles = async () => {
    try {
      const authToken = localStorage.getItem("token");
      const res = await axios.get(`${API_URL}/api/roles`, {
        headers: authToken
          ? { Authorization: `Bearer ${authToken}` }
          : undefined,
      });
      const roleList = res.data || [];
      setRoles(roleList);
      // Auto-default role_id to the "Employee" role if the form has no role yet.
      // Falls back to any role whose name is 'member', then the first role listed.
      setForm((prev) => {
        if (prev.role_id) return prev; // already set (e.g. editing existing employee)
        const employee =
          roleList.find((r) => r.name?.toLowerCase() === "employee") ||
          roleList.find((r) => r.name?.toLowerCase() === "member") ||
          roleList[0];
        return employee ? { ...prev, role_id: String(employee.id) } : prev;
      });
    } catch (err) {
      console.warn("Error fetching roles", err?.message);
    }
  };

  // Manager dropdown options come from the org-chart endpoint so the IDs match
  // what the assign-manager API expects.
  const fetchManagers = async () => {
    try {
      const authToken = localStorage.getItem("token");
      const res = await axios.get(`${API_URL}/api/org-chart/employees`, {
        headers: authToken
          ? { Authorization: `Bearer ${authToken}` }
          : undefined,
      });
      setManagerOptions(res.data?.employees || []);
    } catch (err) {
      console.warn("Error fetching manager options", err?.message);
    }
  };
  const calculateStepCompletion = () => {
    const completion = {};
    Object.keys(stepFields).forEach((step) => {
      const fields = stepFields[step];
      const filled = fields.filter((f) => {
        // Rows, not a form key — counts as filled once one address is typed.
        if (f === "alternate_emails")
          return cleanAltList(altEmails, altDraft).length > 0;
        // Likewise: the skills list lives outside `form`.
        if (f === "skills") return skillRows.length > 0;
        const val = form[f] || files[f];
        return val !== null && val !== undefined && val !== "";
      }).length;
      const total = fields.length;
      completion[step] = {
        filled,
        total,
        status: filled === total ? "Completed" : "In Progress",
      };
    });
    setCompletedCounts(completion);
  };

  const handleChange = (e) => {
    const { name } = e.target;
    let { value } = e.target;
    // readOnly stops typing in the browser; this keeps the locked email safe
    // from any other route into the field (autofill, devtools, paste scripts).
    if (name === "email" && emailLocked) return;
    // Digits only, separators stripped, hard-capped at the stored length — so
    // a pasted "1234 5678 9012" lands clean and typing stops at the cap.
    if (name === "bank_account_number") {
      value = sanitizeAccountNumberInput(value);
    }
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
    setDirty(true);
  };

  // ── Bank IFSC ─────────────────────────────────────────────────────────────
  // What the last lookup wrote into Bank name / Branch. Anything the user has
  // since typed over is theirs, and a later lookup leaves it alone.
  const ifscFilledRef = useRef({});

  const handleIfscChange = (next) => {
    setForm((prev) => ({ ...prev, bank_ifsc: next }));
    setDirty(true);
  };

  // Filling the bank and branch is a convenience, not an authority: it writes
  // only where the field is empty or still holds the previous lookup's answer,
  // so a branch typed by hand — or one the service gets wrong — survives.
  const applyIfscDetails = ({ bank, branch }) => {
    const patch = {};
    const consider = (field, incoming) => {
      if (!incoming) return;
      const current = String(form[field] || "").trim();
      // Occupied by something the user typed → leave it.
      if (current && current !== ifscFilledRef.current[field]) return;
      if (current === incoming) return;
      patch[field] = incoming;
    };
    consider("bank_name", bank);
    consider("bank_branch", branch);
    if (!Object.keys(patch).length) return;
    Object.assign(ifscFilledRef.current, patch);
    setForm((prev) => ({ ...prev, ...patch }));
    setDirty(true);
  };

  // Clear the shared alternate-email error as soon as any row is touched.
  const clearAltEmailError = () => {
    if (!errors.alternate_emails) return;
    setErrors((prev) => {
      const next = { ...prev };
      delete next.alternate_emails;
      return next;
    });
  };

  const setAltEmailError = (message) =>
    setErrors((prev) => ({ ...prev, alternate_emails: message }));

  const handleAltEmailChange = (id, value) => {
    setAltEmails((prev) =>
      prev.map((row) => (row.id === id ? { ...row, value } : row)),
    );
    clearAltEmailError();
    setDirty(true);
  };

  const removeAltEmail = (id) => {
    setAltEmails((prev) => prev.filter((row) => row.id !== id));
    clearAltEmailError();
    setDirty(true);
  };

  // An added row that gets emptied is a removal — no point keeping a blank one
  // around when there is already an open row below it.
  const dropAltEmailIfEmpty = (id) => {
    setAltEmails((prev) =>
      prev.filter((row) => row.id !== id || row.value.trim() !== ""),
    );
  };

  // Enter / Add: move the open row's address into the list and reopen an empty
  // one. Returns false when the address can't be accepted, so the caller knows
  // the row is still holding something.
  const commitAltEmail = () => {
    const value = altDraft.trim();
    if (!value) return true;
    if (!EMAIL_RE.test(value)) {
      setAltEmailError(`"${value}" is not a valid email`);
      return false;
    }
    const key = value.toLowerCase();
    if (key === String(form.email || "").trim().toLowerCase()) {
      setAltEmailError("That is already the login email");
      return false;
    }
    if (altEmails.some((row) => row.value.trim().toLowerCase() === key)) {
      setAltEmailError(`${value} has already been added`);
      return false;
    }
    setAltEmails((prev) => [...prev, makeAltRow(value)]);
    setAltDraft("");
    clearAltEmailError();
    setDirty(true);
    return true;
  };

  const handleAltDraftKeyDown = (e) => {
    if (e.key !== "Enter") return;
    // Enter inside the wizard would otherwise submit the whole form.
    e.preventDefault();
    commitAltEmail();
  };

  // ── Skills ────────────────────────────────────────────────────────────────
  // Everyone may add, edit and remove skills — a member's own record included.
  // Only view mode shows the list without the editor, because nothing on that
  // screen is editable.
  const skillsReadOnly = isView;

  const setSkillField = (key) => (e) => {
    const { value } = e.target;
    setSkillDraft((prev) => ({ ...prev, [key]: value }));
    if (skillError) setSkillError("");
  };

  // Cleared so the next skill can be typed straight away — the tab is meant to
  // be filled in one pass — and so a cancelled edit leaves nothing behind.
  const resetSkillDraft = () => {
    setSkillDraft(emptySkillDraft());
    setSkillDraftFile(null);
    setEditingSkillId(null);
    setSkillError("");
  };

  // Rejected here rather than at submit: the picker is the moment the user can
  // still choose a different file.
  const pickSkillCertificate = (file) => {
    const problem = validateCertificate(file);
    if (problem) {
      setSkillError(problem);
      return;
    }
    setSkillError("");
    setSkillDraftFile(file);
  };

  // Clears both the pending file and the one already on file, so saving the
  // skill afterwards actually detaches the certificate.
  const clearSkillCertificate = () => {
    setSkillDraftFile(null);
    setSkillDraft((prev) => ({ ...prev, certificate: "" }));
  };

  // Add, or apply the edit — one button, because it is one form.
  const commitSkill = () => {
    const name = skillDraft.name.trim();
    if (!name) {
      setSkillError("Enter a skill before adding it.");
      return;
    }
    // The row being edited is allowed to keep its own name.
    if (
      skillRows.some(
        (r) =>
          r.id !== editingSkillId &&
          r.name.toLowerCase() === name.toLowerCase(),
      )
    ) {
      setSkillError(`${name} has already been added.`);
      return;
    }
    if (skillDraft.completed_on && skillDraft.completed_on > todayStr) {
      setSkillError("Completion date can't be in the future.");
      return;
    }
    const values = {
      name,
      level: skillDraft.level,
      program: skillDraft.program.trim(),
      completed_on: skillDraft.completed_on,
      certificate: skillDraft.certificate,
      file: skillDraftFile,
    };
    setSkillRows((prev) =>
      editingSkillId
        ? prev.map((row) =>
            row.id === editingSkillId ? { ...row, ...values } : row,
          )
        : [...prev, makeSkillRow(values)],
    );
    resetSkillDraft();
    setDirty(true);
  };

  const handleSkillKeyDown = (e) => {
    if (e.key !== "Enter") return;
    // Enter anywhere in the wizard would otherwise submit the whole form.
    e.preventDefault();
    commitSkill();
  };

  // Load a card back into the panel. Its savedIndex travels with it so the
  // certificate on file stays openable while it is being edited.
  const startEditSkill = (row) => {
    setEditingSkillId(row.id);
    setSkillDraft({
      name: row.name,
      level: row.level,
      program: row.program,
      completed_on: row.completed_on,
      certificate: row.certificate,
      savedIndex: row.savedIndex,
    });
    setSkillDraftFile(row.file || null);
    setSkillError("");
  };

  const removeSkill = (id) => {
    // Deleting the card under edit would leave the panel editing nothing.
    if (id === editingSkillId) resetSkillDraft();
    setSkillRows((prev) => prev.filter((row) => row.id !== id));
    setDirty(true);
  };

  // Stored certificates stream through the authenticated endpoint like every
  // other employee document — their /uploads/team/* URL is blocked. They are
  // addressed by the skill's index in the saved list, which is why a row
  // added or removed since the last save cannot be opened yet.
  const openSkillCertificate = (row) =>
    openMemberDocument(recordId, "skill_certificate", {
      index: row.savedIndex,
    }).catch((err) =>
      toast.error(
        err?.response?.status === 403
          ? "You do not have access to this document."
          : err?.response?.status === 404
            ? "The certificate file is missing."
            : "Could not open the certificate.",
      ),
    );

  const visibleSkills = useMemo(() => {
    const needle = skillSearch.trim().toLowerCase();
    if (!needle) return skillRows;
    return skillRows.filter(
      (row) =>
        row.name.toLowerCase().includes(needle) ||
        row.level.toLowerCase().includes(needle) ||
        row.program.toLowerCase().includes(needle),
    );
  }, [skillRows, skillSearch]);

  // Inline validation. Runs on submit. Returns the errors map.
  const validate = (data) => {
    const errs = {};
    if (!data.first_name?.trim()) errs.first_name = "Required";
    if (!data.email?.trim()) errs.email = "Required";
    else if (!/^\S+@\S+\.\S+$/.test(data.email))
      errs.email = "Enter a valid email";
    // Alternate emails are optional, but anything typed must be usable and
    // must not just restate the login email.
    const alts = cleanAltList(altEmails, altDraft);
    const badAlt = alts.find((e) => !EMAIL_RE.test(e));
    if (badAlt) {
      errs.alternate_emails = `"${badAlt}" is not a valid email`;
    } else if (
      data.email &&
      alts.some((e) => e.toLowerCase() === data.email.trim().toLowerCase())
    ) {
      errs.alternate_emails =
        "An alternate email can't repeat the primary email";
    }
    // Compare the 'YYYY-MM-DD' strings directly — Date parsing of a date-only
    // string is UTC, which makes "today" look like tomorrow east of UTC.
    if (data.dob && data.dob > todayStr) {
      errs.dob = "Date of birth can't be in the future";
    }
    // DOB as printed on the Aadhaar card — same rule.
    if (data.aadhar_dob && data.aadhar_dob > todayStr) {
      errs.aadhar_dob = "Date of birth can't be in the future";
    }
    const join = data.join_date || data.doj;
    if (join && join > todayStr) {
      errs.doj = "Joining date can't be in the future";
    }
    if (data.salary && Number.isNaN(Number(data.salary))) {
      errs.salary = "Annual CTC must be a number";
    }
    // Bank account number is optional — HR routinely adds an employee before
    // their bank details arrive — but anything typed must be storable. Checked
    // on the value with separators stripped, matching the server.
    const account = validateAccountNumber(data.bank_account_number, {
      required: false,
    });
    if (!account.ok) {
      errs.bank_account_number = account.error;
    }
    // Employee type is optional. Stored as an employment_types id; the server
    // accepts blank and only rejects a non-empty value that isn't a real id.
    if (join && data.exit_date && data.exit_date < join) {
      errs.exit_date = "Exit date must be after joining date";
    }
    return errs;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    const name = e.target.name;

    setFiles((prev) => ({
      ...prev,
      [name]: file,
    }));
    setDirty(true);

    if (name === "profile_photo" && file) {
      const reader = new FileReader();
      reader.onload = () => showPhoto(reader.result);
      reader.readAsDataURL(file);
    }
  };

  // Adapter for FileUpload — accepts a File directly and feeds it through the
  // existing handleFileChange so dirty tracking + photo preview keep working.
  const selectFile = (name, file) => {
    if (!file) return;
    handleFileChange({ target: { name, files: [file] } });
  };

  // FileUpload wrapper that injects the member id so existing protected
  // documents open through the authenticated stream endpoint (their static
  // /uploads/team/* URL is blocked). Harmless for public fields like
  // profile_photo, which FileUpload keeps on a plain URL.
  const DocUpload = (props) => (
    <FileUpload memberId={recordId} readOnly={stepLocked} {...props} />
  );

  // Clear an existing or pending file. Sends "" so the backend stores NULL.
  const handleFileRemove = (name) => {
    setFiles((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
    setForm((prev) => ({ ...prev, [name]: "" }));
    if (name === "profile_photo") showPhoto(null);
    setDirty(true);
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1) setCurrentStep((s) => s + 1);
  };
  const prevStep = () => {
    if (currentStep > 0) setCurrentStep((s) => s - 1);
  };
  // const formatDate = (d) => {
  //   if (!d) return "";
  //   const date = new Date(d);
  //   date.setMinutes(date.getMinutes() + date.getTimezoneOffset());
  //   return date.toISOString().split("T")[0];
  // };
  const handleSubmit = async (e, redirect = true) => {
    if (e) e.preventDefault();
    setSubmitErr(null);

    // Client-side validation. If any error, jump the user to the step
    // that contains the first invalid field.
    const errs = validate(form);
    if (Object.keys(errs).length) {
      setErrors(errs);
      const stepOf = {
        first_name: 0,
        last_name: 0,
        email: 0,
        alternate_emails: 0,
        dob: 0,
        doj: 1,
        join_date: 1,
        salary: 1,
        employee_type: 1,
        bank_account_number: 3,
        exit_date: 4,
      };
      const firstInvalid = Object.keys(errs)[0];
      if (stepOf[firstInvalid] != null) setCurrentStep(stepOf[firstInvalid]);
      setSubmitErr("Please fix the highlighted fields.");
      return;
    }

    setSaving(true);
    try {
      const fd = new FormData();

      Object.entries(form).forEach(([key, val]) => {
        // Don't send the password field if it's empty — backend treats
        // empty as "don't change". Also skip null/undefined.
        if (key === "password" && (!val || !String(val).trim())) return;
        // Owned by the skills list below, not by `form` — appending the stale
        // form value here would overwrite what the list serialises.
        if (SKILL_OWNED_FIELDS.has(key)) return;
        // country_code is a UI-only field — combined into contact_number.
        if (key === "country_code") return;
        if (key === "contact_number") {
          const num = String(val || "").trim();
          if (!num) return;
          // Avoid double-prefixing if the user typed the code into the input.
          const cc = (form.country_code || "").trim();
          const combined = cc && !num.startsWith("+") ? `${cc} ${num}` : num;
          fd.append("contact_number", combined);
          return;
        }
        if (val !== null && val !== undefined) {
          fd.append(key, val);
        }
      });

      // One field carries every alternate address as a JSON array. Always
      // sent — including "[]" — so removing the last one actually persists.
      fd.append(
        "alternate_emails",
        JSON.stringify(cleanAltList(altEmails, altDraft)),
      );

      // The whole skills list as JSON, always sent so clearing it persists.
      // The legacy single-certification columns go with it, mirrored from the
      // first skill that has one; the server derives them again, but sending
      // them keeps a plain API client honest too.
      fd.append("skills", serializeSkills(skillRows));
      const legacy = legacyCertification(skillRows);
      fd.append("program_name", legacy.program_name);
      fd.append("comp_date", legacy.comp_date);
      fd.append("certificate", legacy.certificate);

      // Certificates picked but not yet uploaded. They travel under one field,
      // so the row each belongs to is sent beside them, in the same order.
      const certificateSlots = [];
      const skillCertificateFiles = [];
      skillRows.forEach((row, index) => {
        if (row.file instanceof File) {
          skillCertificateFiles.push(row.file);
          certificateSlots.push(index);
        }
      });
      fd.append("skill_certificate_slots", JSON.stringify(certificateSlots));

      // Documents go up ahead of the save, in slices, and only their ids ride
      // on the request — exactly what the onboarding wizard does, and for the
      // same reason: the reverse proxy in front of the API refuses a body over
      // ~1 MB with its own 413, so a couple of scanned PDFs on one PUT never
      // reached Node at all (see api/chunkedUpload).
      //
      // The onboarding endpoint claims a narrower set of field names and
      // rejects anything outside it, so that path keeps posting its files
      // directly — it is reached with a photo and little else.
      const picked = Object.entries(files).filter(([, f]) => f instanceof File);
      const uploads = {};
      const uploadedIds = [];
      const viaOnboarding = mode === "onboarding";
      const totalUploads = viaOnboarding
        ? 0
        : picked.length + skillCertificateFiles.length;
      let uploadedSoFar = 0;
      const note = () => {
        uploadedSoFar += 1;
        setUploadNote(`Uploading document ${uploadedSoFar} of ${totalUploads}…`);
      };

      try {
        if (viaOnboarding) {
          picked.forEach(([field, file]) => fd.append(field, file));
          skillCertificateFiles.forEach((file) =>
            fd.append("skill_certificates", file),
          );
        }
        for (const [field, file] of viaOnboarding ? [] : picked) {
          note();
          // Serial: the proxy wants small bodies, not many at once.
          // eslint-disable-next-line no-await-in-loop
          const id = await uploadFileInChunks(file);
          uploads[field] = id;
          uploadedIds.push(id);
        }
        if (!viaOnboarding && skillCertificateFiles.length) {
          const ids = [];
          for (const file of skillCertificateFiles) {
            note();
            // eslint-disable-next-line no-await-in-loop
            const id = await uploadFileInChunks(file);
            ids.push(id);
            uploadedIds.push(id);
          }
          // An array, in the order the slots above name.
          uploads.skill_certificates = ids;
        }
      } catch (uploadErr) {
        // Nothing has claimed these yet, so release them rather than leaving
        // part files to age out — and to keep the per-user session cap free
        // for the retry the user is about to make.
        // Called with the id alone — forEach would also hand it the index and
        // the array, which is not what cancelChunkedUpload takes.
        uploadedIds.forEach((id) => cancelChunkedUpload(id));
        setUploadNote(null);
        throw uploadErr;
      }

      if (totalUploads) setUploadNote("Saving…");
      if (Object.keys(uploads).length) {
        fd.append("uploads", JSON.stringify(uploads));
      }

      const authToken = localStorage.getItem("token");
      const config = {
        headers: { Authorization: `Bearer ${authToken}` },
      };

      let res;

      if (mode === "onboarding") {
        res = await axios.post(
          `${API_URL}/api/onboarding/complete`,
          fd,
          config,
        );
        if (res.data.token) localStorage.setItem("token", res.data.token);
        localStorage.setItem("user", JSON.stringify(res.data.user));
        localStorage.setItem("userdata", JSON.stringify(res.data.user));
        if (redirect) {
          window.location.href = "/member/dashboard";
          return;
        }
        setDirty(false);
        setSubmitErr(null);
        return;
      }

      let savedEmployeeId = recordId;
      let savedSkills;
      if (recordId) {
        const updated = await axios.put(
          `${API_URL}/api/team/${recordId}`,
          fd,
          config,
        );
        savedSkills = updated?.data?.skills;
      } else {
        const created = await axios.post(`${API_URL}/api/team`, fd, config);
        savedEmployeeId = created?.data?.id || created?.data?.insertId || null;
        savedSkills = created?.data?.skills;
        setCreatedId(savedEmployeeId);
      }

      // Re-read the list as it was stored: the pending certificates now have
      // server paths, and every row has the index the stream endpoint wants.
      // Without this, saving twice would upload the same file again.
      if (savedSkills !== undefined && savedSkills !== null) {
        setSkillRows(parseSkills(savedSkills));
      } else {
        // Older endpoint that doesn't echo the row back — the server stores the
        // list in the order it was sent, so the indices are known anyway.
        setSkillRows((prev) =>
          prev.map((row, index) => ({ ...row, file: null, savedIndex: index })),
        );
      }

      // Persist manager relationships via the org-chart endpoint.
      if (
        savedEmployeeId &&
        (form.primary_manager_id !== "" || form.dotted_manager_id !== "")
      ) {
        try {
          await axios.post(
            `${API_URL}/api/org-chart/assign-manager`,
            {
              employee_id: Number(savedEmployeeId),
              primary_manager_id: form.primary_manager_id
                ? Number(form.primary_manager_id)
                : null,
              dotted_manager_id: form.dotted_manager_id
                ? Number(form.dotted_manager_id)
                : null,
            },
            config,
          );
        } catch (mgrErr) {
          console.warn(
            "Manager assignment failed:",
            mgrErr?.response?.data?.error || mgrErr.message,
          );
        }
      }

      setDirty(false);
      setSubmitErr(null);

      if (redirect) {
        // Saving leaves the form exactly where it is — same tab, same scroll —
        // so a long record can be filled in several passes. The toast is the
        // only thing that happens; closing is the user's move. `onSaved` is a
        // notification for the parent (refresh a list, say), NOT a cue to
        // navigate — EmployeeEditPage used to route away here.
        setSavedToast(true);
        setTimeout(() => setSavedToast(false), 2200);
        if (onSaved) onSaved({ id: savedEmployeeId });
      }
    } catch (err) {
      console.error(err?.response?.data || err);
      if (err?.response?.status === 401) {
        // Don't bin the form — show a banner so the user can re-login and retry.
        setSessionExpired(true);
        setSubmitErr(
          "Your session expired. Please log in again to save changes.",
        );
        return;
      }
      setSubmitErr(
        err?.response?.data?.error || err.message || "Could not save changes.",
      );
    } finally {
      setSaving(false);
      setUploadNote(null);
    }
  };
  // Options
  const genderOptions = ["Select", "Male", "Female", "Other"];
  const relationOptions = [
    "Select Relation",
    "Father",
    "Mother",
    "Brother",
    "Sister",
    "Spouse",
    "Friend",
  ];
  const bloodOptions = [
    "Select Blood Group",
    "A+",
    "A-",
    "B+",
    "B-",
    "AB+",
    "AB-",
    "O+",
    "O-",
  ];
  const nationalityOptions = [
    "Enter Nationality",
    "American",
    "Canadian",
    "Indian",
    "British",
    "Australian",
    "German",
    "French",
    "Italian",
    "Chinese",
    "Japanese",
    "Other",
  ];

  const SavedToast = () =>
    savedToast ? (
      <div className="form-saved-toast" role="status" aria-live="polite">
        <span className="ckmark">✓</span>
        <div>
          <b>Saved</b>
          <small>Profile updated</small>
        </div>
      </div>
    ) : null;

  return (
    <div className={`main-wrap ${isView ? "is-view-mode" : ""}`}>
      <SavedToast />
      <div className="main-container employee-forms">
        <div
          className="title-wrap"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div>
            <h2>
              {steps[currentStep]} Information{" "}
              {isView && <span className="view-badge">VIEW MODE</span>}
            </h2>
            <p>
              {isView
                ? "Read-only employee details."
                : "Enter the Employee's Information."}
            </p>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {isView && onEdit && (
              <button type="button" className="org-gradi" onClick={onEdit}>
                Edit
              </button>
            )}
            {onClose && (
              <button type="button" className="btn-cancel" onClick={guardClose}>
                {dirty && !isView ? "Discard & close" : "Close"}
              </button>
            )}
          </div>
        </div>

        {sessionExpired ? (
          <div className="form-banner form-banner-warn" role="alert">
            <span>
              ⚠ Your session expired. Please log in again to save changes.
            </span>
            <button
              type="button"
              className="form-banner-btn"
              onClick={() => {
                localStorage.clear();
                window.location.assign("/login");
              }}
            >
              Log in again
            </button>
          </div>
        ) : submitErr ? (
          <div className="form-banner form-banner-err" role="alert">
            ⚠ {submitErr}
          </div>
        ) : null}

        <div className="form-content">
          {/* Tabs */}
          <div className="form-tabs">
            <ul>
              {steps.map((step, index) => (
                <li
                  key={index}
                  className={`tab-item ${index === currentStep ? "active" : ""}`}
                  onClick={() => {
                    if (mode === "onboarding" && index > currentStep) return;
                    setCurrentStep(index);
                  }}
                >
                  {step}
                </li>
              ))}
            </ul>
          </div>

          <form onSubmit={handleSubmit}>
            {stepLocked && (
              <div className="form-banner form-banner-warn" role="status">
                🔒 Only HR can change these details. Contact HR if something
                here needs updating — your Personal tab stays editable.
              </div>
            )}

            {/* Everything a member may not change sits inside this fieldset:
                one `disabled` switch covers every input, select and button
                within, so no field can be missed as the form grows. */}
            <fieldset className="step-lock" disabled={stepLocked}>
            {/* =================== Step Fields =================== */}
            {/* Personal */}
            {currentStep === 0 && (
              <div className="form-step">
                <div className="form-item full photo-upload">
                  <label>Profile Photo</label>
                  <div className="photo-upload-row">
                    <div className="photo-avatar">
                      {previewPhoto && !photoBroken ? (
                        // A freshly picked file is a data: URI; a stored one is
                        // a path that must be resolved off UPLOAD_URL — in
                        // production the API is proxied at the web root while
                        // uploads only exist under /backend, so building this
                        // off API_URL 404s.
                        <img
                          src={
                            previewPhoto.startsWith("data:")
                              ? previewPhoto
                              : photoUrl(previewPhoto)
                          }
                          alt={form.first_name || "Profile"}
                          onError={() => setPhotoBroken(true)}
                        />
                      ) : (
                        <span className="photo-avatar-fallback">
                          {(form.first_name?.[0] || "").toUpperCase()}
                          {(form.last_name?.[0] || "").toUpperCase() || "?"}
                        </span>
                      )}
                    </div>
                    <div className="photo-actions">
                      <input
                        type="file"
                        name="profile_photo"
                        accept="image/*"
                        onChange={handleFileChange}
                        ref={photoInputRef}
                        style={{ display: "none" }}
                      />
                      <button
                        type="button"
                        className="photo-btn primary"
                        onClick={() => photoInputRef.current?.click()}
                      >
                        {previewPhoto ? "Change photo" : "Upload photo"}
                      </button>
                      {previewPhoto && (
                        <button
                          type="button"
                          className="photo-btn"
                          onClick={() => handleFileRemove("profile_photo")}
                        >
                          Remove
                        </button>
                      )}
                      <small className="photo-hint">
                        JPG or PNG · max ~5 MB
                      </small>
                    </div>
                  </div>
                </div>
                <div className="form-item">
                  <label>
                    First Name <span className="req">*</span>
                  </label>
                  <input
                    name="first_name"
                    value={form.first_name || ""}
                    onChange={handleChange}
                    placeholder="Enter First Name"
                    aria-invalid={!!errors.first_name}
                    required
                  />
                  {errors.first_name && (
                    <span className="field-error">{errors.first_name}</span>
                  )}
                </div>
                <div className="form-item">
                  <label>Last Name</label>
                  <input
                    name="last_name"
                    value={form.last_name || ""}
                    onChange={handleChange}
                    placeholder="Enter Last Name"
                  />
                </div>
                <div className="form-item">
                  <label>
                    Email <span className="req">*</span>
                    {emailLocked && (
                      <span className="verified-tag" title="Verified email — cannot be changed">
                        <BadgeCheck size={12} aria-hidden="true" />
                        Verified
                      </span>
                    )}
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={form.email || ""}
                    onChange={handleChange}
                    placeholder="Enter Email"
                    aria-invalid={!!errors.email}
                    readOnly={emailLocked}
                    required
                  />
                  {emailLocked && (
                    <span className="field-hint">
                      This is the verified login email and cannot be edited.
                    </span>
                  )}
                  {errors.email && (
                    <span className="field-error">{errors.email}</span>
                  )}
                </div>
                {!isOnboarding && (
                  <div className="form-item">
                    <label>Password</label>
                    <input
                      type="password"
                      name="password"
                      placeholder="Leave blank to keep current"
                      value={form.password || ""}
                      onChange={handleChange}
                      autoComplete="new-password"
                    />
                  </div>
                )}
                <div className="form-item full alt-emails">
                  <div className="alt-emails-panel">
                    <label>
                      Alternate email
                      <span className="alt-emails-opt">Optional</span>
                    </label>
                    <p className="alt-emails-help">
                      For personal or previous work addresses. Your login email
                      is unaffected.
                    </p>
                    {altEmails.map((row, i) => (
                      <div className="alt-email-row" key={row.id}>
                        <Mail
                          className="alt-email-ic"
                          size={15}
                          aria-hidden="true"
                        />
                        <input
                          type="email"
                          value={row.value}
                          onChange={(e) =>
                            handleAltEmailChange(row.id, e.target.value)
                          }
                          onBlur={() => dropAltEmailIfEmpty(row.id)}
                          placeholder="name@example.com"
                          aria-label={`Alternate email ${i + 1}`}
                          aria-invalid={!!errors.alternate_emails}
                        />
                        {!isView && (
                          <button
                            type="button"
                            className="alt-email-x"
                            onClick={() => removeAltEmail(row.id)}
                            title="Remove"
                            aria-label={`Remove ${
                              row.value.trim() || `alternate email ${i + 1}`
                            }`}
                          >
                            <X size={15} aria-hidden="true" />
                          </button>
                        )}
                      </div>
                    ))}
                    {/* The open row. Committing it reopens an empty one, which
                        is why there is no separate "add" button. */}
                    {!isView && (
                      <div className="alt-email-row is-draft">
                        <Mail
                          className="alt-email-ic"
                          size={15}
                          aria-hidden="true"
                        />
                        <input
                          type="email"
                          value={altDraft}
                          onChange={(e) => {
                            setAltDraft(e.target.value);
                            clearAltEmailError();
                          }}
                          onKeyDown={handleAltDraftKeyDown}
                          placeholder="name@example.com"
                          aria-label="Add an alternate email"
                          aria-invalid={!!errors.alternate_emails}
                        />
                        <button
                          type="button"
                          className="alt-email-commit"
                          onClick={commitAltEmail}
                          disabled={!altDraft.trim()}
                        >
                          Add
                        </button>
                      </div>
                    )}
                    {errors.alternate_emails ? (
                      <span className="field-error">
                        {errors.alternate_emails}
                      </span>
                    ) : (
                      <span className="alt-emails-count">
                        {altEmails.length
                          ? `${altEmails.length} added`
                          : "None added"}
                      </span>
                    )}
                  </div>
                </div>
                <div className="form-item">
                  <label>Contact Number</label>
                  <div className="phone-row">
                    <select
                      name="country_code"
                      className="phone-cc"
                      value={form.country_code || "+91"}
                      onChange={handleChange}
                    >
                      {COUNTRY_CODES.map((c) => (
                        <option key={c.code + c.label} value={c.code}>
                          {c.label} {c.code}
                        </option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      name="contact_number"
                      className="phone-num"
                      value={form.contact_number || ""}
                      onChange={handleChange}
                      placeholder="Enter Contact Number"
                      pattern="[0-9 +()\-]*"
                    />
                  </div>
                </div>
                <div className="form-item">
                  <label>Date of Birth</label>
                  <input
                    type="date"
                    name="dob"
                    max={todayStr}
                    value={form.dob || ""}
                    onChange={handleChange}
                    aria-invalid={!!errors.dob}
                  />
                  {errors.dob && (
                    <span className="field-error">{errors.dob}</span>
                  )}
                </div>
                <div className="form-item">
                  <label>Gender</label>
                  <select
                    name="gender"
                    value={form.gender || ""}
                    onChange={handleChange}
                  >
                    {genderOptions.map((g) => (
                      <option key={g} value={g === "Select" ? "" : g}>
                        {g}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-item">
                  <label>Nationality</label>
                  <select
                    name="nationality"
                    value={form.nationality || ""}
                    onChange={handleChange}
                  >
                    {nationalityOptions.map((na) => (
                      <option
                        key={na}
                        value={na === "Enter Nationality" ? "" : na}
                      >
                        {na}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-item full">
                  <label>Address</label>
                  <textarea
                    name="address"
                    value={form.address || ""}
                    onChange={handleChange}
                    placeholder="Enter Address"
                  />
                </div>

                <div className="form-item">
                  <label>Blood Group</label>
                  <select
                    name="blood_group"
                    value={form.blood_group || ""}
                    onChange={handleChange}
                  >
                    {bloodOptions.map((r) => (
                      <option
                        key={r}
                        value={r === "Select Blood Group" ? "" : r}
                      >
                        {r}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-item">
                  <label>Emergency Contact Name</label>
                  <input
                    name="emergency_contact_name"
                    value={form.emergency_contact_name || ""}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-item">
                  <label>Relation</label>
                  <select
                    name="emergency_contact_relation"
                    value={form.emergency_contact_relation || ""}
                    onChange={handleChange}
                  >
                    {relationOptions.map((r) => (
                      <option key={r} value={r === "Select Relation" ? "" : r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-item">
                  <label>Contact Number</label>
                  <input
                    name="emergency_contact_phone"
                    value={form.emergency_contact_phone || ""}
                    onChange={handleChange}
                  />
                </div>
              </div>
            )}

            {/* Employment */}
            {currentStep === 1 && (
              <div className="form-step">
                <div className="form-item">
                  <label>Employee Id</label>
                  <input
                    name="employeeid"
                    placeholder="Enter Id"
                    disabled={isLocked("employeeid")}
                    value={form.employeeid || ""}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-item">
                  <label>Role</label>
                  <select
                    name="role_id"
                    value={form.role_id || ""}
                    onChange={handleChange}
                  >
                    <option value="">Select Role</option>
                    {roles.map((r) => (
                      <option key={r.id} value={String(r.id)}>
                        {r.name}
                        {r.description ? ` — ${r.description}` : ""}
                      </option>
                    ))}
                  </select>
                  <small
                    style={{
                      color: "var(--em-fg5)",
                      marginTop: 4,
                      display: "block",
                    }}
                  >
                    Controls what dashboard and permissions the employee gets on
                    login.
                  </small>
                </div>
                <div className="form-item">
                  <label>Job Title</label>

                  <select
                    name="jobtitle"
                    value={form.jobtitle}
                    onChange={handleChange}
                  >
                    <option value="">Select Job Title</option>
                    {jobTitles.map((j) => (
                      <option key={j.id} value={j.id}>
                        {j.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-item">
                  <label>Date of Joining</label>
                  <input
                    type="date"
                    name="doj"
                    max={todayStr}
                    value={form.doj || ""}
                    onChange={handleChange}
                    aria-invalid={!!errors.doj}
                  />
                  {errors.doj && (
                    <span className="field-error">{errors.doj}</span>
                  )}
                </div>
                <div className="form-item">
                  <label>Department</label>
                  <select
                    name="department"
                    value={form.department || ""}
                    onChange={handleChange}
                  >
                    <option value="">Select Department</option>
                    {departments.map((r) => (
                      <option
                        key={r.id}
                        value={r === "Select Department" ? "" : r.id}
                      >
                        {r.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-item">
                  <label>Employee Type</label>
                  <select
                    name="employee_type"
                    value={form.employee_type || ""}
                    onChange={handleChange}
                    aria-invalid={!!errors.employee_type}
                  >
                    <option value="">Select Employee Type</option>
                    {employmentTypes.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                  {errors.employee_type && (
                    <span className="field-error">{errors.employee_type}</span>
                  )}
                </div>
                {!isOnboarding && (
                  <>
                    <div className="form-item">
                      <label>Primary Manager</label>
                      <select
                        name="primary_manager_id"
                        value={form.primary_manager_id || ""}
                        onChange={handleChange}
                      >
                        <option value="">No manager (root)</option>
                        {managerOptions
                          .filter((m) => !editData || m.id !== editData.id)
                          .map((m) => (
                            <option key={m.id} value={m.id}>
                              {m.name}
                              {m.designation ? ` · ${m.designation}` : ""}
                            </option>
                          ))}
                      </select>
                    </div>
                    <div className="form-item">
                      <label>Dotted-line Manager (optional)</label>
                      <select
                        name="dotted_manager_id"
                        value={form.dotted_manager_id || ""}
                        onChange={handleChange}
                      >
                        <option value="">None</option>
                        {managerOptions
                          .filter((m) => !editData || m.id !== editData.id)
                          .map((m) => (
                            <option key={m.id} value={m.id}>
                              {m.name}
                              {m.designation ? ` · ${m.designation}` : ""}
                            </option>
                          ))}
                      </select>
                    </div>
                  </>
                )}
                <div className="form-item">
                  <label>Employee Status</label>
                  <select
                    name="employee_status"
                    value={form.employee_status || ""}
                    onChange={handleChange}
                  >
                    <option value="">Employee Status</option>
                    {employmentStatus.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-item">
                  <label>Work Location</label>
                  <select
                    name="work_location"
                    value={form.work_location || ""}
                    onChange={handleChange}
                  >
                    <option value="">Select work location</option>
                    {workLocations.map((loc) => (
                      <option key={loc.id} value={loc.id}>
                        {loc.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-item">
                  <label>Work Shift/ Working Hours</label>
                  <select
                    name="work_shift"
                    value={form.work_shift || ""}
                    onChange={handleChange}
                  >
                    <option value="">Select work Shift</option>
                    {workShifts.map((shift) => (
                      <option key={shift.id} value={shift.id}>
                        {shift.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-item">
                  <label>Annual CTC</label>
                  <input
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    min="0"
                    name="salary"
                    placeholder="Annual cost to company"
                    value={form.salary || ""}
                    onChange={handleChange}
                    aria-invalid={!!errors.salary}
                  />
                  <small className="field-hint">
                    Total yearly cost to company — payroll derives monthly pay
                    from this.
                  </small>
                  {errors.salary && (
                    <span className="field-error">{errors.salary}</span>
                  )}
                </div>
                <div className="form-item">
                  <label>Hourly Cost (Workspace)</label>
                  <input
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    min="0"
                    name="hourly_cost"
                    placeholder="Cost per hour"
                    value={form.hourly_cost || ""}
                    onChange={handleChange}
                  />
                  <small style={{ color: "#9ca3af", fontSize: 11 }}>
                    Used to calculate project cost &amp; profitability.
                  </small>
                </div>
                <div className="form-item full">
                  <label>Additional Notes</label>
                  <textarea
                    name="additional_notes"
                    placeholder="Note..."
                    value={form.additional_notes || ""}
                    onChange={handleChange}
                  />
                </div>
              </div>
            )}

            {/* Skills — an editor on the left, the list it fills on the right.
                Each skill carries its own proficiency, program, completion
                date and certificate; the whole list is one column. */}
            {currentStep === 2 && (
              <div
                className={`form-step skills-step ${skillsReadOnly ? "is-readonly" : ""}`}
              >
                {!skillsReadOnly && (
                  <div className="skill-add">
                    <h4 className="skill-add-head">
                      {editingSkillId ? "Edit skill" : "Add a skill"}
                    </h4>

                    <div className="form-item full">
                      <label htmlFor="skill-name">Skill / expertise</label>
                      <input
                        id="skill-name"
                        placeholder="e.g. TypeScript"
                        value={skillDraft.name}
                        onChange={setSkillField("name")}
                        onKeyDown={handleSkillKeyDown}
                        aria-invalid={!!skillError}
                      />
                    </div>

                    <div className="form-item full">
                      <label htmlFor="skill-level">Proficiency</label>
                      <select
                        id="skill-level"
                        value={skillDraft.level}
                        onChange={setSkillField("level")}
                      >
                        {PROFICIENCY_LEVELS.map((level) => (
                          <option key={level} value={level}>
                            {level}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="skill-add-pair">
                      <div className="form-item full">
                        <label htmlFor="skill-program">Program name</label>
                        <input
                          id="skill-program"
                          placeholder="Optional"
                          value={skillDraft.program}
                          onChange={setSkillField("program")}
                          onKeyDown={handleSkillKeyDown}
                        />
                      </div>
                      <div className="form-item full">
                        <label htmlFor="skill-date">Completion date</label>
                        <input
                          id="skill-date"
                          type="date"
                          max={todayStr}
                          value={skillDraft.completed_on}
                          onChange={setSkillField("completed_on")}
                        />
                      </div>
                    </div>

                    <FileUpload
                      name="skill_certificate_draft"
                      label="Certificate"
                      accept={SKILL_CERT_ACCEPT}
                      hint="PDF or image · max 5 MB"
                      value={skillDraft.certificate}
                      pendingFile={skillDraftFile}
                      onChange={pickSkillCertificate}
                      onRemove={clearSkillCertificate}
                      // Not a document column of its own, so the panel hands in
                      // its own opener instead of FileUpload deriving one.
                      onOpen={
                        !skillDraftFile &&
                        skillDraft.certificate &&
                        recordId != null &&
                        skillDraft.savedIndex != null
                          ? () => openSkillCertificate(skillDraft)
                          : undefined
                      }
                    />

                    {skillError && (
                      <span className="field-error skill-error">
                        {skillError}
                      </span>
                    )}

                    <div className="skill-add-actions">
                      <button
                        type="button"
                        className="skill-add-btn"
                        onClick={commitSkill}
                      >
                        {editingSkillId ? "Update skill" : "Add skill"}
                      </button>
                      {editingSkillId && (
                        <button
                          type="button"
                          className="skill-cancel-btn"
                          onClick={resetSkillDraft}
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                    <p className="skill-add-note">
                      {editingSkillId
                        ? "The card updates as soon as you apply the change — the employee record itself is written when you save the form."
                        : "Form clears after each add, so several skills can be entered in a row without leaving the tab."}
                    </p>
                  </div>
                )}

                <div
                  className={`skill-list ${skillsReadOnly ? "is-readonly" : ""}`}
                >
                  <div className="skill-list-head">
                    <h4>
                      Added skills
                      <span className="skill-count">{skillRows.length}</span>
                    </h4>
                    <input
                      type="search"
                      className="skill-search"
                      placeholder="Search skills"
                      value={skillSearch}
                      onChange={(e) => setSkillSearch(e.target.value)}
                      aria-label="Search skills"
                    />
                  </div>

                  {skillRows.length === 0 ? (
                    <p className="skill-empty">
                      {skillsReadOnly
                        ? "No skills recorded yet."
                        : "No skills yet — add the first one on the left."}
                    </p>
                  ) : visibleSkills.length === 0 ? (
                    <p className="skill-empty">
                      Nothing matches “{skillSearch.trim()}”.
                    </p>
                  ) : (
                    <div className="skill-cards">
                      {visibleSkills.map((row) => {
                        const fileName = row.file
                          ? row.file.name
                          : certificateName(row.certificate);
                        const completed = formatSkillDate(row.completed_on);
                        // Only a saved certificate can be opened — a pending
                        // one has not reached the server yet.
                        const openable =
                          !row.file &&
                          !!row.certificate &&
                          recordId != null &&
                          row.savedIndex != null;
                        return (
                          <div
                            className={`skill-card ${
                              editingSkillId === row.id ? "is-editing" : ""
                            }`}
                            key={row.id}
                          >
                            <div className="skill-card-top">
                              <span className="skill-card-name" title={row.name}>
                                {row.name}
                              </span>
                              {!skillsReadOnly && (
                                <div className="skill-card-actions">
                                  <button
                                    type="button"
                                    className="skill-card-act"
                                    onClick={() => startEditSkill(row)}
                                    aria-label={`Edit ${row.name}`}
                                    title={`Edit ${row.name}`}
                                  >
                                    <Pencil size={13} />
                                  </button>
                                  <button
                                    type="button"
                                    className="skill-card-act danger"
                                    onClick={() => removeSkill(row.id)}
                                    aria-label={`Remove ${row.name}`}
                                    title={`Remove ${row.name}`}
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                              )}
                            </div>

                            <span
                              className={`skill-level is-${levelTone(row.level)}`}
                            >
                              {row.level}
                            </span>

                            <div className="skill-card-meta">
                              {row.program ? (
                                <b>{row.program}</b>
                              ) : (
                                <span className="skill-meta-none">—</span>
                              )}
                              <small>
                                {completed && <>Completed {completed} · </>}
                                {!fileName ? (
                                  "No certificate attached"
                                ) : openable ? (
                                  <button
                                    type="button"
                                    className="skill-cert-link"
                                    onClick={() => openSkillCertificate(row)}
                                    title={`View ${fileName}`}
                                  >
                                    {fileName}
                                  </button>
                                ) : (
                                  <span title={fileName}>
                                    {fileName}
                                    {row.file ? " (pending upload)" : ""}
                                  </span>
                                )}
                              </small>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Documents */}
            {currentStep === 3 && (
              <div className="form-steps docu-wrap">
                <div className="form-group">
                  <DocUpload
                    name="resume"
                    label="Resume / CV"
                    accept=".pdf,.doc,.docx,image/*"
                    hint="PDF, DOC, or image"
                    value={form.resume}
                    pendingFile={files.resume}
                    onChange={(f) => selectFile("resume", f)}
                    onRemove={() => handleFileRemove("resume")}
                  />
                  <DocUpload
                    name="edu_certificates"
                    label="Education Certificates"
                    accept=".pdf,image/*"
                    hint="PDF or image"
                    value={form.edu_certificates}
                    pendingFile={files.edu_certificates}
                    onChange={(f) => selectFile("edu_certificates", f)}
                    onRemove={() => handleFileRemove("edu_certificates")}
                  />
                  <DocUpload
                    name="other_edu_certificates"
                    label="Other Education Certificates"
                    accept=".pdf,image/*"
                    hint="PDF or image"
                    value={form.other_edu_certificates}
                    pendingFile={files.other_edu_certificates}
                    onChange={(f) => selectFile("other_edu_certificates", f)}
                    onRemove={() => handleFileRemove("other_edu_certificates")}
                  />
                </div>

                <div className="form-group">
                  <h4> Previous Company Documents </h4>
                  <DocUpload
                    name="salary_slip"
                    label="Last 3 Months Salary Slip"
                    accept=".pdf,image/*"
                    hint="PDF or image"
                    value={form.salary_slip}
                    pendingFile={files.salary_slip}
                    onChange={(f) => selectFile("salary_slip", f)}
                    onRemove={() => handleFileRemove("salary_slip")}
                  />
                  <DocUpload
                    name="reli_experience"
                    label="Relieving / Experience Letter"
                    accept=".pdf,image/*"
                    hint="PDF or image"
                    value={form.reli_experience}
                    pendingFile={files.reli_experience}
                    onChange={(f) => selectFile("reli_experience", f)}
                    onRemove={() => handleFileRemove("reli_experience")}
                  />
                </div>
                <div className="form-group">
                  <h4> Company Documents </h4>

                  <DocUpload
                    name="offer_letter"
                    label="Offer Letter"
                    accept=".pdf,image/*"
                    hint="PDF or image"
                    value={form.offer_letter}
                    pendingFile={files.offer_letter}
                    onChange={(f) => selectFile("offer_letter", f)}
                    onRemove={() => handleFileRemove("offer_letter")}
                  />
                  <DocUpload
                    name="confirmation_letter"
                    label="Confirmation Letter"
                    accept=".pdf,image/*"
                    hint="PDF or image"
                    value={form.confirmation_letter}
                    pendingFile={files.confirmation_letter}
                    onChange={(f) => selectFile("confirmation_letter", f)}
                    onRemove={() => handleFileRemove("confirmation_letter")}
                  />
                  <DocUpload
                    name="appointment_letter"
                    label="Appointment Letter"
                    accept=".pdf,image/*"
                    hint="PDF or image"
                    value={form.appointment_letter}
                    pendingFile={files.appointment_letter}
                    onChange={(f) => selectFile("appointment_letter", f)}
                    onRemove={() => handleFileRemove("appointment_letter")}
                  />
                  <DocUpload
                    name="employment_contract"
                    label="Employment Contract"
                    accept=".pdf,image/*"
                    hint="PDF or image"
                    value={form.employment_contract}
                    pendingFile={files.employment_contract}
                    onChange={(f) => selectFile("employment_contract", f)}
                    onRemove={() => handleFileRemove("employment_contract")}
                  />
                  <DocUpload
                    name="nda_agreement"
                    label="NDA or Confidential Agreement"
                    accept=".pdf,image/*"
                    hint="PDF or image"
                    value={form.nda_agreement}
                    pendingFile={files.nda_agreement}
                    onChange={(f) => selectFile("nda_agreement", f)}
                    onRemove={() => handleFileRemove("nda_agreement")}
                  />
                </div>
                <div className="form-group">
                  <h4> Personal Documents </h4>

                  <DocUpload
                    name="pan_card"
                    label="PAN Card"
                    accept=".pdf,image/*"
                    hint="PDF or image"
                    value={form.pan_card}
                    pendingFile={files.pan_card}
                    onChange={(f) => selectFile("pan_card", f)}
                    onRemove={() => handleFileRemove("pan_card")}
                  />
                  <div className="form-item">
                    <label>Pan Card Number</label>
                    <input
                      name="pan_card_number"
                      placeholder="000000000"
                      value={form.pan_card_number || ""}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-item">
                    <label>Name</label>
                    <input
                      name="pan_card_name"
                      placeholder="Full Name"
                      value={form.pan_card_name || ""}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-item">
                    <label>Nominee Name</label>
                    <input
                      name="nominee_name"
                      placeholder="Name"
                      value={form.nominee_name || ""}
                      onChange={handleChange}
                    />
                  </div>
                  <DocUpload
                    name="aadhar_card"
                    label="Aadhar Card"
                    accept=".pdf,image/*"
                    hint="PDF or image"
                    value={form.aadhar_card}
                    pendingFile={files.aadhar_card}
                    onChange={(f) => selectFile("aadhar_card", f)}
                    onRemove={() => handleFileRemove("aadhar_card")}
                  />
                  <div className="form-item">
                    <label>Name</label>
                    <input
                      name="aadhar_name"
                      placeholder="Name"
                      value={form.aadhar_name || ""}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-item">
                    <label>Aadhar Number</label>
                    <input
                      name="aadhar_card_number"
                      placeholder="0000 0000 0000"
                      value={form.aadhar_card_number || ""}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-item">
                    <label>DOB</label>
                    <input
                      type="date"
                      name="aadhar_dob"
                      max={todayStr}
                      value={form.aadhar_dob || ""}
                      onChange={handleChange}
                      aria-invalid={!!errors.aadhar_dob}
                    />
                    {errors.aadhar_dob && (
                      <span className="field-error">{errors.aadhar_dob}</span>
                    )}
                  </div>
                  <h4> Bank Details </h4>

                  <div className="form-item">
                    <label>Bank Name</label>
                    <input
                      name="bank_name"
                      placeholder="Name"
                      value={form.bank_name || ""}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-item">
                    <label>Enter Full Name</label>
                    <input
                      name="bank_full_name"
                      placeholder="Enter full name as per account"
                      value={form.bank_full_name || ""}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-item">
                    <label>Account Number</label>
                    <input
                      name="bank_account_number"
                      placeholder="000000000"
                      value={form.bank_account_number || ""}
                      onChange={handleChange}
                      inputMode="numeric"
                      autoComplete="off"
                      maxLength={accountNumberMaxLength()}
                      aria-invalid={!!errors.bank_account_number}
                    />
                    {errors.bank_account_number && (
                      <span className="field-error">
                        {errors.bank_account_number}
                      </span>
                    )}
                  </div>
                  <div className="form-item">
                    <label>Branch</label>
                    <input
                      name="bank_branch"
                      placeholder="Branch"
                      value={form.bank_branch || ""}
                      onChange={handleChange}
                    />
                  </div>
                  <IfscField
                    id="bank_ifsc"
                    name="bank_ifsc"
                    label="IFSC code"
                    value={form.bank_ifsc || ""}
                    onChange={handleIfscChange}
                    onResolved={applyIfscDetails}
                  />
                </div>
              </div>
            )}

            {/* Exit */}
            {steps[currentStep] === "Exit" && mode !== "onboarding" && (
              <div className="form-step">
                <div className="form-item">
                  <label>Resignation Date</label>
                  <input
                    type="date"
                    name="resignation_date"
                    value={form.resignation_date || ""}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-item">
                  <label>Last Working Date</label>
                  <input
                    type="date"
                    name="exit_date"
                    min={form.join_date || form.doj || undefined}
                    value={form.exit_date || ""}
                    onChange={handleChange}
                    aria-invalid={!!errors.exit_date}
                  />
                  {errors.exit_date && (
                    <span className="field-error">{errors.exit_date}</span>
                  )}
                </div>

                <div className="form-item">
                  <label>Notice Period</label>
                  <input
                    type="number"
                    name="notice_period"
                    value={form.notice_period || ""}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-item">
                  <label>Reason for Exit</label>
                  <input
                    name="exit_reason_input"
                    value={form.exit_reason_input || ""}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-item full">
                  <label>Exit Interview Notes</label>
                  <textarea
                    name="exit_reason"
                    value={form.exit_reason || ""}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-item full">
                  <p>Offboarding Checklist</p>
                  <div className="chec-boxes">
                    <p>
                      <label>
                        <input type="checkbox" />
                        <span>Company Property Returned</span>
                      </label>
                    </p>
                    <p>
                      <label>
                        <input type="checkbox" />
                        <span>Access Revoked</span>
                      </label>
                    </p>
                    <p>
                      <label>
                        <input type="checkbox" />
                        <span>Exit Interview Completed</span>
                      </label>
                    </p>
                    <p>
                      <label>
                        <input type="checkbox" />
                        <span>Final Settlement Processed</span>
                      </label>
                    </p>
                  </div>
                </div>

                <DocUpload
                  name="exit_document"
                  label="Exit Document"
                  accept=".pdf,image/*"
                  hint="PDF or image"
                  value={form.exit_document}
                  pendingFile={files.exit_document}
                  onChange={(f) => selectFile("exit_document", f)}
                  onRemove={() => handleFileRemove("exit_document")}
                />

                <div className="form-item">
                  <label>Status</label>
                  <input
                    name="exit_status"
                    placeholder="e.g., Resigned / Terminated"
                    value={form.exit_status || ""}
                    onChange={handleChange}
                  />
                </div>
              </div>
            )}

            </fieldset>

            {/* Navigation */}
            <div className="form-nav">
              {currentStep > 0 && (
                <button type="button" onClick={prevStep}>
                  Go Back
                </button>
              )}
              {!isView && (
                <button
                  type="button"
                  className="btn-fill"
                  onClick={(e) => handleSubmit(e, true)}
                  disabled={saving}
                >
                  {/* Once the first save has created the record, the button
                      is updating it — even though the wizard was opened
                      without editData. */}
                  {saving
                    ? uploadNote || "Saving…"
                    : recordId
                      ? "Save changes"
                      : currentStep < steps.length - 1
                        ? "Save & continue"
                        : "Submit"}
                </button>
              )}
              {currentStep < steps.length - 1 && (
                <button type="button" onClick={nextStep}>
                  Next
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

      {/* Sidebar */}
      <div className="left-sidebar">
        <div className="progress-sidebar">
          <h3>Progress</h3>
          <div className="progress-wrap">
            <div className="progress-sub">
              <ul className="progress-menu">
                {steps.map((s, i) => {
                  const info = completedCounts[i] || {};
                  return (
                    <li
                      key={i}
                      className={
                        i < currentStep
                          ? "done"
                          : i === currentStep
                            ? "progress"
                            : "next-step"
                      }
                    >
                      <div className="sidebar-step">
                        <span>{s}</span>
                        <span
                          className={`status ${info.status === "Completed" ? "completed" : "in-progress"}`}
                        >
                          {info.status || "In Progress"}
                        </span>
                      </div>
                      <div className="sidebar-count">
                        ({info.filled || 0}/{info.total || 0})
                      </div>
                      <i></i>
                    </li>
                  );
                })}
              </ul>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ height: `${progress}%` }}
                ></div>
              </div>
            </div>
            <div className="progress-label">
              {Math.round(progress)}% Complete
            </div>
          </div>
          <div className="line-space"></div>
          {/* Sidebar Save removed — Save button now lives in the form footer
            so it's adjacent to the fields a user just edited. */}
        </div>
      </div>
    </div>
  );
};

export default TeamFormWizard;
